/**
 * Modules & Parameters admin tab — Slice 9.9 (#12 Configurable QRA)
 *
 * 2026-05-20 redesign (v3): direct contribution-% selector + ordered modules.
 *
 * Design rationale:
 *   - Engine math is Σ(value × weight) / Σ(weights) × 100, scale-invariant.
 *     Only the RATIO between weights in the same module matters.
 *   - Abstract labels like "Low / High" mean different things in different
 *     modules — a single "High" parameter in a 4-param module ≠ a "High"
 *     parameter in a 9-param module, even with the same weight value.
 *   - So the selector exposes the contribution % DIRECTLY — that's already
 *     module-aware (it's computed from THIS module's weights).
 *   - User picks "this parameter should drive ~30% of the module score";
 *     the system computes the weight that achieves it given the other
 *     parameters' current weights.
 *   - Numeric input stays available behind a "Custom…" option for power users.
 *   - Modules display in a fixed natural-flow order, but the database
 *     display_name is preserved exactly — no display label overrides.
 *
 * What did NOT change:
 *   - Backend API endpoints, request bodies, and response shapes
 *   - Engine math
 *   - Module display_name (database value used verbatim)
 *   - Weight profiles table layout
 *   - RBAC (still admin-only via the existing requireRole middleware)
 *   - Audit logging (mutations flow through the existing PUT endpoint)
 */

import { TIER_LABEL } from '../lib/riskVocabulary';
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAdminReadOnly } from '../lib/admin-read-only';

// =============================================================================
// Constants
// =============================================================================

/**
 * Contribution-% presets the user can pick from. The numeric weight needed
 * to hit each percentage is computed at use-time from the OTHER parameters'
 * weights, so the same "30%" choice means "30%" no matter the module.
 *
 * Targets are chosen to span the meaningful range. Equal contribution
 * across N parameters is the default; lowering one rebalances the others
 * automatically (because the engine normalizes by Σ weights).
 *
 * "Disabled" maps to weight = 0 (contributes nothing). "Equal share" sets
 * this parameter's weight equal to the average of the other inputs so it
 * matches them exactly — useful when you want N parameters to weigh the same.
 */
const CONTRIBUTION_PRESETS: ReadonlyArray<{
  /** Target % the user picks. -1 sentinel = Custom (numeric weight). */
  pct: number;
  label: string;
  hint: string;
}> = [
  { pct: 0,        label: 'Disabled (0%)',     hint: 'Parameter contributes nothing to this module score.' },
  { pct: 5,        label: 'Minor (5%)',        hint: 'Background factor — slight nudge to the final score.' },
  { pct: 10,       label: 'Small (10%)',       hint: 'Smaller-than-equal share.' },
  { pct: -2,       label: 'Equal share',       hint: 'Match the average of the other input parameters in this module.' }, // sentinel -2
  { pct: 20,       label: 'Modest (20%)',      hint: 'Slightly above an equal 4-parameter share.' },
  { pct: 30,       label: 'Notable (30%)',     hint: 'Roughly 3× the contribution of a typical parameter in a 4-input module.' },
  { pct: 40,       label: 'Major (40%)',       hint: 'Dominates a 4-parameter module — about 2.7× an equal share.' },
  { pct: 50,       label: 'Half (50%)',        hint: 'This parameter alone is worth all the others combined.' },
  { pct: 70,       label: 'Dominant (70%)',    hint: 'Heavily skews the module toward this parameter. Use rarely.' },
];

const CUSTOM_OPTION = {
  pct: -1,
  label: 'Custom (advanced)…',
  hint: 'Enter a raw numeric weight directly. Power-user mode — most users should use the percentage presets above.',
};

const EQUAL_SHARE_SENTINEL = -2;

/**
 * Display order for modules on this admin page. Maps to existing module.code
 * values; the database display_name is used verbatim (no label override).
 * Modules not listed here fall through to the end alphabetically — so a
 * newly-added module never disappears.
 */
const MODULE_DISPLAY_ORDER: ReadonlyArray<string> = [
  'timeline',
  'data_risk',
  'supply_chain',
  'migration_feasibility',
  'threat',
  'cryptography',
  'compliance',
  'integrated_foundation',
  'integrated_advanced',
];

// =============================================================================
// Math helpers
// =============================================================================

/**
 * Compute the weight value needed for `thisBinding` so that its contribution
 * to the module score becomes exactly `targetPct` percent, given the OTHER
 * input bindings' current weights.
 *
 * Derivation. We want:
 *     w_new / (w_new + Σ others) = targetPct / 100
 *  => 100·w_new = targetPct·(w_new + Σ others)
 *  => 100·w_new − targetPct·w_new = targetPct·Σ others
 *  => w_new = (targetPct / (100 − targetPct)) × Σ others
 *
 * Special cases:
 *  - targetPct === 0    →  w_new = 0 (Disabled). No division.
 *  - targetPct === 100  →  impossible (would require infinite weight). Capped
 *                          to a high finite value with a warning surfaced by
 *                          the caller.
 *  - Σ others === 0     →  any positive weight gives this parameter 100% of
 *                          the score by itself; pick 1 by convention.
 *  - EQUAL_SHARE_SENTINEL → set w_new = average(other input weights).
 *
 * Values are rounded to 4 decimals to match the storage precision.
 */
function weightForTargetPct(
  targetPct: number,
  otherInputBindings: ReadonlyArray<{ effective_weight: number }>,
): number {
  const sumOthers = otherInputBindings.reduce((s, b) => s + b.effective_weight, 0);

  if (targetPct === 0) return 0;
  if (targetPct === EQUAL_SHARE_SENTINEL) {
    if (otherInputBindings.length === 0) return 1;
    const avg = sumOthers / otherInputBindings.length;
    return Math.round(avg * 10000) / 10000 || 1;
  }
  if (targetPct >= 99.99) {
    // Cap at 99% to avoid runaway weights. Any sane user wanting >99% should
    // disable the other parameters instead.
    return Math.round(((99 / 1) * (sumOthers || 1)) * 10000) / 10000;
  }
  if (sumOthers === 0) {
    // The other parameters are all disabled. Any positive weight makes this
    // parameter contribute 100%. Use 1 as a stable default.
    return 1;
  }

  const w = (targetPct / (100 - targetPct)) * sumOthers;
  return Math.round(w * 10000) / 10000;
}

/**
 * Compute the effective contribution percentage for one binding within
 * its module. Sums up only the input-role bindings — gates and exclusions
 * don't contribute to the weighted sum.
 */
function computeContributionPct(
  thisBinding: ParameterBinding,
  allBindings: ReadonlyArray<ParameterBinding>,
): number | null {
  if (thisBinding.role !== 'Input') return null;
  const inputs = allBindings.filter((b) => b.role === 'Input');
  const total = inputs.reduce((s, b) => s + b.effective_weight, 0);
  if (total <= 0) return null;
  return (thisBinding.effective_weight / total) * 100;
}

/**
 * Format a numeric weight for display. Drops trailing zeros — "2" not "2.0000".
 */
function formatWeight(weight: number): string {
  if (Math.abs(weight - Math.round(weight)) < 1e-9) return String(Math.round(weight));
  return String(Number(weight.toFixed(4)));
}

/**
 * Match a current contribution % to the nearest preset for the initial
 * selector value. Tolerance of ±2.5% so users see "Modest (20%)" if their
 * stored value is currently producing ~19% or ~21%.
 */
function pctToInitialPreset(currentPct: number | null): number {
  if (currentPct === null) return CUSTOM_OPTION.pct;
  if (Math.abs(currentPct) < 0.001) return 0;
  let best = CUSTOM_OPTION.pct;
  let bestDist = Infinity;
  for (const p of CONTRIBUTION_PRESETS) {
    if (p.pct < 0) continue; // skip sentinels
    const d = Math.abs(p.pct - currentPct);
    if (d < bestDist && d < 2.5) {
      best = p.pct;
      bestDist = d;
    }
  }
  return best;
}

// =============================================================================
// Types — mirror the backend payload
// =============================================================================

interface ParameterBinding {
  binding_id: string;
  parameter_id: string;
  parameter_code: string;
  parameter_display_name: string;
  parameter_unit: string | null;
  parameter_data_type: string;
  parameter_description: string | null;
  role: string;
  default_weight: number;
  effective_weight: number;
  weight_entry_id: string | null;
  is_mandatory: boolean;
  display_order: number;
  local_display_name: string | null;
}

interface ModuleRow {
  id: string;
  code: string;
  display_name: string;
  description: string | null;
  module_family: string;
  status: string;
  config: {
    config_id: string;
    version: number;
    scoring_function_family: string;
  } | null;
  bindings: ParameterBinding[];
}

interface WeightEntry {
  entry_id: string;
  weight: number;
  is_module_level: boolean;
  binding_id: string | null;
  integrated_child_module_id: string | null;
  parameter_code: string | null;
  parameter_display_name: string | null;
  module_code: string | null;
  module_display_name: string | null;
  child_module_code: string | null;
  child_module_display_name: string | null;
}

interface WeightProfile {
  id: string;
  name: string;
  scope_kind: string;
  version: number;
  status: string;
  is_current: boolean;
  description: string | null;
  entries: WeightEntry[];
}

// =============================================================================
// Main export
// =============================================================================

export function ModulesAdminTab() {
  const [activeSection, setActiveSection] = useState<'modules' | 'profiles'>('modules');

  return (
    <div>
      <div className="text-xs text-ink-500 mb-4 max-w-3xl leading-relaxed">
        Adjust how strongly each parameter contributes to its module's score.
        Module scores are weighted averages of their inputs — pick the
        contribution percentage you want this parameter to drive, and the
        system computes the weight automatically. Changes take effect on the
        next assessment run.
      </div>

      <div className="flex gap-2 mb-6">
        {(['modules', 'profiles'] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setActiveSection(s)}
            className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${
              activeSection === s
                ? 'bg-accent/10 border-accent/30 text-accent'
                : 'bg-white border-ink-200 text-ink-600 hover:bg-ink-50'
            }`}
          >
            {s === 'modules' ? 'Modules & Parameters' : 'Weight Profiles'}
          </button>
        ))}
      </div>

      {activeSection === 'modules' && <ModulesSection />}
      {activeSection === 'profiles' && <WeightProfilesSection />}
    </div>
  );
}

// =============================================================================
// Modules Section
// =============================================================================

function ModulesSection() {
  const q = useQuery({
    queryKey: ['admin', 'modules'],
    queryFn: () => api.get<ModuleRow[]>('/admin/modules'),
  });

  const [expandedModule, setExpandedModule] = useState<string | null>(null);

  // Order modules per MODULE_DISPLAY_ORDER; anything else goes to the bottom
  // alphabetically. No display-name override — what the database has is what
  // the user sees.
  const orderedModules = useMemo(() => {
    if (!q.data) return [];
    const byCode = new Map(q.data.map((m) => [m.code, m]));
    const ordered: ModuleRow[] = [];
    const used = new Set<string>();

    for (const code of MODULE_DISPLAY_ORDER) {
      const mod = byCode.get(code);
      if (mod) {
        ordered.push(mod);
        used.add(code);
      }
    }
    const remaining = q.data
      .filter((m) => !used.has(m.code))
      .sort((a, b) => a.display_name.localeCompare(b.display_name));
    for (const mod of remaining) ordered.push(mod);

    return ordered;
  }, [q.data]);

  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorPanel error={q.error} />;
  if (orderedModules.length === 0) {
    return <div className="text-sm text-ink-400 italic py-4">No modules registered.</div>;
  }

  return (
    <div className="border border-ink-200 rounded-sm overflow-hidden divide-y divide-ink-100 bg-white">
      {orderedModules.map((mod, idx) => (
        <ModuleRowComponent
          key={mod.id}
          mod={mod}
          ordinal={idx + 1}
          expanded={expandedModule === mod.id}
          onToggle={() =>
            setExpandedModule(expandedModule === mod.id ? null : mod.id)
          }
        />
      ))}
    </div>
  );
}

function ModuleRowComponent({
  mod,
  ordinal,
  expanded,
  onToggle,
}: {
  mod: ModuleRow;
  ordinal: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  const familyColor: Record<string, string> = {
    Foundation: 'bg-blue-50 text-blue-700 border-blue-200',
    Advanced: 'bg-purple-50 text-purple-700 border-purple-200',
    Integrated: 'bg-teal-50 text-teal-700 border-teal-200',
  };
  // module_family is a stored identifier; readers see the agreed terms.
  const familyLabel: Record<string, string> = {
    Foundation: TIER_LABEL.foundation,
    Advanced: TIER_LABEL.advanced,
    Integrated: 'Composite',
  };

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-ink-50/50 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-xs text-ink-400 font-mono w-6 shrink-0">
            {String(ordinal).padStart(2, '0')}
          </span>
          <span
            className={`text-[10px] px-2 py-0.5 rounded-sm border font-medium shrink-0 ${
              familyColor[mod.module_family] ?? 'bg-ink-50 text-ink-500 border-ink-200'
            }`}
          >
            {familyLabel[mod.module_family] ?? mod.module_family}
          </span>
          <div className="min-w-0">
            <div className="text-sm font-medium text-ink-900 truncate">
              {mod.display_name}
            </div>
            <div className="text-xs text-ink-400 font-mono">{mod.code}</div>
          </div>
        </div>
        <div className="flex items-center gap-4 shrink-0 ml-4">
          {mod.config && (
            <span className="text-[10px] text-ink-400 font-mono">
              {mod.config.scoring_function_family} · v{mod.config.version}
            </span>
          )}
          <span className="text-xs text-ink-400">
            {mod.bindings.length} parameter{mod.bindings.length !== 1 ? 's' : ''}
          </span>
          <span className="text-ink-400 text-xs">{expanded ? '▲' : '▼'}</span>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-ink-100 bg-ink-50/30 px-4 py-3">
          {mod.description && (
            <p className="text-xs text-ink-500 mb-3 max-w-3xl">{mod.description}</p>
          )}
          {mod.bindings.length === 0 ? (
            <div className="text-xs text-ink-400 italic py-2">
              No parameter bindings for this module.
            </div>
          ) : (
            <BindingsTable moduleId={mod.id} bindings={mod.bindings} />
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Bindings table
// =============================================================================

function BindingsTable({
  moduleId,
  bindings,
}: {
  moduleId: string;
  bindings: ParameterBinding[];
}) {
  return (
    <div className="border border-ink-200 rounded-sm overflow-hidden bg-white">
      <table className="w-full text-xs">
        <thead className="bg-ink-50 border-b border-ink-200">
          <tr className="text-left text-ink-400 uppercase tracking-wide text-[10px]">
            <th className="px-3 py-2">Parameter</th>
            <th className="px-3 py-2">Role</th>
            <th className="px-3 py-2 w-44">Influence</th>
            <th className="px-3 py-2 w-32">Contribution</th>
            <th className="px-3 py-2 w-28">Action</th>
          </tr>
        </thead>
        <tbody>
          {bindings.map((b) => (
            <BindingRow
              key={b.binding_id}
              moduleId={moduleId}
              binding={b}
              allBindings={bindings}
            />
          ))}
        </tbody>
      </table>
      <div className="px-3 py-2 text-[10px] text-ink-400 border-t border-ink-100 bg-ink-50/40 leading-relaxed">
        Module score = (Σ value × weight) ÷ (Σ weights) × 100. Only Input-role
        parameters contribute. Contribution % shows each parameter's actual
        share of the module score given the current weights.
      </div>
    </div>
  );
}

function BindingRow({
  moduleId,
  binding,
  allBindings,
}: {
  moduleId: string;
  binding: ParameterBinding;
  allBindings: ParameterBinding[];
}) {
  const readOnly = useAdminReadOnly();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const contribPct = computeContributionPct(binding, allBindings);

  // Initial preset selection — match current contribution % to nearest preset.
  // Gate/exclusion roles can't be expressed as a contribution %, so they
  // always start in Custom mode.
  const [selectedPct, setSelectedPct] = useState<number>(() => {
    if (binding.role !== 'Input') return CUSTOM_OPTION.pct;
    return pctToInitialPreset(contribPct);
  });
  const [customDraft, setCustomDraft] = useState<string>(formatWeight(binding.effective_weight));
  const [rationale, setRationale] = useState<string>('');

  // Bindings used to compute target-% weights — exclude THIS one.
  const otherInputs = useMemo(
    () => allBindings
      .filter((b) => b.role === 'Input' && b.binding_id !== binding.binding_id)
      .map((b) => ({ effective_weight: b.effective_weight })),
    [allBindings, binding.binding_id],
  );

  const mutation = useMutation({
    mutationFn: ({ weight, rationale }: { weight: number; rationale: string }) =>
      api.put<{ data: { updated_target: string } }>(
        `/admin/modules/${moduleId}/parameters/${binding.binding_id}/effective-weight`,
        { weight, override_rationale: rationale },
      ),
    onSuccess: () => {
      setEditing(false);
      setSaveError(null);
      setRationale('');
      qc.invalidateQueries({ queryKey: ['admin', 'modules'] });
      qc.invalidateQueries({ queryKey: ['admin', 'weight-profiles'] });
    },
    onError: (err) => {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    },
  });

  /**
   * Compute the weight that the current selection would produce. Used both
   * for the "Preview" line under the dropdown AND for the save call.
   */
  function resolveWeightFromSelection(): { weight: number; error: string | null } {
    if (selectedPct === CUSTOM_OPTION.pct) {
      const n = parseFloat(customDraft);
      if (Number.isNaN(n) || n < 0 || n > 9999) {
        return { weight: 0, error: 'Custom weight must be a number between 0 and 9999' };
      }
      return { weight: n, error: null };
    }
    return { weight: weightForTargetPct(selectedPct, otherInputs), error: null };
  }

  function handleSave() {
    const { weight, error } = resolveWeightFromSelection();
    if (error) { setSaveError(error); return; }
    setSaveError(null);
    if (rationale.trim().length < 10) {
      setSaveError('Please record why this weight is changing (at least 10 characters).');
      return;
    }
    mutation.mutate({ weight, rationale: rationale.trim() });
  }

  function handleCancel() {
    if (binding.role === 'Input') {
      setSelectedPct(pctToInitialPreset(contribPct));
    } else {
      setSelectedPct(CUSTOM_OPTION.pct);
    }
    setCustomDraft(formatWeight(binding.effective_weight));
    setSaveError(null);
    setRationale('');
    setEditing(false);
  }

  function handleResetToDefault() {
    setSelectedPct(CUSTOM_OPTION.pct);
    setCustomDraft(formatWeight(binding.default_weight));
  }

  const showResetLink = Math.abs(binding.effective_weight - binding.default_weight) > 1e-9;

  // Preview what the save will actually do
  const previewResolved = editing ? resolveWeightFromSelection() : { weight: 0, error: null };

  return (
    <tr className="border-t border-ink-100 hover:bg-ink-50/30 align-top">
      {/* Parameter column */}
      <td className="px-3 py-3 text-ink-800">
        <div className="font-medium">
          {binding.local_display_name ?? binding.parameter_display_name}
          {binding.is_mandatory && (
            <span className="ml-2 text-[9px] text-red-600 uppercase">required</span>
          )}
        </div>
        <div className="text-[10px] font-mono text-ink-400 mt-0.5">
          {binding.parameter_code} · {binding.parameter_data_type}
          {binding.parameter_unit && ` · ${binding.parameter_unit}`}
        </div>
        {binding.parameter_description && (
          <div className="text-[11px] text-ink-500 mt-1 max-w-md leading-relaxed">
            {binding.parameter_description}
          </div>
        )}
      </td>

      {/* Role pill */}
      <td className="px-3 py-3"><RolePill role={binding.role} /></td>

      {/* Influence */}
      <td className="px-3 py-3">
        {editing ? (
          <div className="space-y-1.5">
            <select
              value={selectedPct}
              onChange={(e) => setSelectedPct(Number(e.target.value))}
              className="w-full text-xs border border-accent rounded-sm px-2 py-1 bg-white"
              autoFocus
              disabled={binding.role !== 'Input' && selectedPct !== CUSTOM_OPTION.pct}
            >
              {binding.role === 'Input' && CONTRIBUTION_PRESETS.map((p) => (
                <option key={p.pct} value={p.pct}>{p.label}</option>
              ))}
              <option value={CUSTOM_OPTION.pct}>{CUSTOM_OPTION.label}</option>
            </select>
            {selectedPct === CUSTOM_OPTION.pct && (
              <input
                type="number"
                step="0.01"
                min="0"
                max="9999"
                value={customDraft}
                onChange={(e) => setCustomDraft(e.target.value)}
                className="w-full text-xs border border-ink-300 rounded-sm px-2 py-1 font-mono"
                placeholder="Enter raw weight 0–9999"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSave();
                  if (e.key === 'Escape') handleCancel();
                }}
              />
            )}
            {/* A weighting decision nobody recorded a reason for cannot be
                defended later. The rationale is stored on the weight entry and
                travels with the assessments that used it. */}
            <textarea
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              rows={2}
              className="w-full text-xs border border-ink-300 rounded-sm px-2 py-1 leading-snug"
              placeholder="Why is this weight changing? (recorded against the assessment)"
              onKeyDown={(e) => {
                if (e.key === 'Escape') handleCancel();
              }}
            />
            {/* Live preview of what'll happen on save */}
            {!previewResolved.error && (
              <div className="text-[10px] text-ink-500 leading-snug">
                {selectedPct === CUSTOM_OPTION.pct
                  ? CUSTOM_OPTION.hint
                  : (CONTRIBUTION_PRESETS.find((p) => p.pct === selectedPct)?.hint ?? '')}
                <div className="mt-0.5 font-mono text-ink-400">
                  → weight will be set to {formatWeight(previewResolved.weight)}
                </div>
              </div>
            )}
            {showResetLink && (
              <button
                type="button"
                onClick={handleResetToDefault}
                className="text-[10px] text-accent hover:underline"
                title={`Reset to system default (${formatWeight(binding.default_weight)})`}
              >
                Reset to default
              </button>
            )}
          </div>
        ) : (
          <div>
            {binding.role === 'Input' ? (
              contribPct === null ? (
                <span className="text-xs text-ink-400 italic">—</span>
              ) : binding.effective_weight === 0 ? (
                <span className="text-sm text-ink-500">Disabled</span>
              ) : (
                <span className="text-sm font-medium text-ink-800">
                  ~{contribPct.toFixed(0)}% of module score
                </span>
              )
            ) : (
              <span className="text-xs text-ink-500 italic">
                {binding.role.replace(/_/g, ' ')} — not weighted-sum
              </span>
            )}
            <div className="text-[10px] font-mono text-ink-400 mt-0.5">
              raw weight = {formatWeight(binding.effective_weight)}
              {showResetLink && (
                <span className="ml-2 text-ink-300">
                  (default: {formatWeight(binding.default_weight)})
                </span>
              )}
            </div>
          </div>
        )}
      </td>

      {/* Contribution % column (visualization) */}
      <td className="px-3 py-3">
        {contribPct === null ? (
          <span className="text-[10px] text-ink-300 italic">—</span>
        ) : binding.effective_weight === 0 ? (
          <span className="text-[10px] text-ink-400 italic">0%</span>
        ) : (
          <div className="space-y-1">
            <div className="text-sm font-medium text-ink-700 font-mono">
              {contribPct.toFixed(1)}%
            </div>
            <div className="w-20 h-1 bg-ink-100 rounded-sm overflow-hidden">
              <div
                className="h-full bg-accent/60"
                style={{ width: `${Math.min(100, contribPct)}%` }}
              />
            </div>
          </div>
        )}
      </td>

      {/* Action */}
      <td className="px-3 py-3">
        {editing ? (
          <div className="flex flex-col gap-1">
            <button
              type="button"
              onClick={handleSave}
              disabled={mutation.isPending || !!previewResolved.error}
              className="text-[11px] px-2 py-0.5 bg-accent text-white rounded-sm hover:bg-accent/90 disabled:opacity-50"
            >
              {mutation.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="text-[11px] px-2 py-0.5 text-ink-600 border border-ink-200 rounded-sm hover:bg-ink-50"
            >
              Cancel
            </button>
          </div>
        ) : readOnly ? null : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-[11px] text-accent hover:underline"
          >
            Edit weight
          </button>
        )}
        {saveError && (
          <div className="text-[10px] text-red-600 mt-0.5 max-w-32 leading-snug">{saveError}</div>
        )}
      </td>
    </tr>
  );
}

// =============================================================================
// Weight Profiles Section — module-level weights for integrated_*
// =============================================================================

function WeightProfilesSection() {
  const q = useQuery({
    queryKey: ['admin', 'weight-profiles'],
    queryFn: () => api.get<WeightProfile[]>('/admin/modules/weight-profiles'),
  });

  const [expandedProfile, setExpandedProfile] = useState<string | null>(null);

  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorPanel error={q.error} />;
  if (!q.data || q.data.length === 0) {
    return (
      <div className="border border-ink-200 rounded-sm p-6 text-center text-sm text-ink-400">
        No system weight profiles found.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {q.data.map((profile) => (
        <div key={profile.id} className="border border-ink-200 rounded-sm overflow-hidden">
          <button
            type="button"
            onClick={() =>
              setExpandedProfile(expandedProfile === profile.id ? null : profile.id)
            }
            className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-ink-50/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-ink-900">{profile.name}</span>
                  {profile.is_current && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm">
                      current
                    </span>
                  )}
                  <span className="text-[10px] px-1.5 py-0.5 bg-ink-50 text-ink-500 border border-ink-200 rounded-sm">
                    {profile.status}
                  </span>
                </div>
                <div className="text-xs text-ink-400 font-mono">
                  v{profile.version} · {profile.scope_kind} · {profile.entries.length} entries
                </div>
              </div>
            </div>
            <span className="text-ink-400 text-xs">
              {expandedProfile === profile.id ? '▲' : '▼'}
            </span>
          </button>

          {expandedProfile === profile.id && (
            <div className="border-t border-ink-100">
              <WeightEntriesTable profileId={profile.id} entries={profile.entries} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function WeightEntriesTable({
  profileId,
  entries,
}: {
  profileId: string;
  entries: WeightEntry[];
}) {
  if (entries.length === 0) {
    return (
      <div className="px-4 py-3 text-xs text-ink-400 italic">
        No weight entries in this profile.
      </div>
    );
  }

  // Group by module, respecting MODULE_DISPLAY_ORDER for the grouping order
  const byModule = new Map<string, { sortKey: number; moduleCode: string; entries: WeightEntry[] }>();
  const codeToOrder = new Map<string, number>();
  MODULE_DISPLAY_ORDER.forEach((code, idx) => codeToOrder.set(code, idx));

  for (const e of entries) {
    const moduleCode = e.module_code ?? e.child_module_code ?? '';
    const key = e.module_display_name ?? e.child_module_display_name ?? '(module-level)';
    if (!byModule.has(key)) {
      byModule.set(key, {
        sortKey: codeToOrder.get(moduleCode) ?? 999,
        moduleCode,
        entries: [],
      });
    }
    byModule.get(key)!.entries.push(e);
  }

  const sortedGroups = Array.from(byModule.entries()).sort(
    (a, b) => a[1].sortKey - b[1].sortKey,
  );

  return (
    <div>
      {sortedGroups.map(([moduleName, group]) => (
        <div key={moduleName} className="border-b border-ink-100 last:border-b-0">
          <div className="px-4 py-1.5 bg-ink-50/60 text-[10px] font-medium text-ink-500 uppercase tracking-wide">
            {moduleName}
          </div>
          <table className="w-full text-xs">
            <tbody>
              {group.entries.map((entry) => (
                <WeightEntryRow
                  key={entry.entry_id}
                  profileId={profileId}
                  entry={entry}
                  siblings={group.entries}
                />
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function WeightEntryRow({
  profileId,
  entry,
  siblings,
}: {
  profileId: string;
  entry: WeightEntry;
  siblings: ReadonlyArray<WeightEntry>;
}) {
  const readOnly = useAdminReadOnly();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Contribution % within the profile group (e.g. within integrated_advanced
  // siblings).
  const totalInGroup = siblings.reduce((s, e) => s + e.weight, 0);
  const currentPct = totalInGroup > 0 ? (entry.weight / totalInGroup) * 100 : null;

  const [selectedPct, setSelectedPct] = useState<number>(() => pctToInitialPreset(currentPct));
  const [customDraft, setCustomDraft] = useState<string>(formatWeight(entry.weight));
  const [rationale, setRationale] = useState<string>('');

  const otherSiblings = useMemo(
    () => siblings
      .filter((s) => s.entry_id !== entry.entry_id)
      .map((s) => ({ effective_weight: s.weight })),
    [siblings, entry.entry_id],
  );

  const mutation = useMutation({
    mutationFn: ({ weight, rationale }: { weight: number; rationale: string }) =>
      api.put(
        `/admin/modules/weight-profiles/${profileId}/entries/${entry.entry_id}`,
        { weight, override_rationale: rationale },
      ),
    onSuccess: () => {
      setEditing(false);
      setSaveError(null);
      setRationale('');
      qc.invalidateQueries({ queryKey: ['admin', 'weight-profiles'] });
      qc.invalidateQueries({ queryKey: ['admin', 'modules'] });
    },
    onError: (err) => {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    },
  });

  function resolveWeightFromSelection(): { weight: number; error: string | null } {
    if (selectedPct === CUSTOM_OPTION.pct) {
      const n = parseFloat(customDraft);
      if (Number.isNaN(n) || n < 0 || n > 9999) {
        return { weight: 0, error: 'Custom weight must be between 0 and 9999' };
      }
      return { weight: n, error: null };
    }
    return { weight: weightForTargetPct(selectedPct, otherSiblings), error: null };
  }

  function handleSave() {
    const { weight, error } = resolveWeightFromSelection();
    if (error) { setSaveError(error); return; }
    setSaveError(null);
    if (rationale.trim().length < 10) {
      setSaveError('Please record why this weight is changing (at least 10 characters).');
      return;
    }
    mutation.mutate({ weight, rationale: rationale.trim() });
  }

  function handleCancel() {
    setSelectedPct(pctToInitialPreset(currentPct));
    setCustomDraft(formatWeight(entry.weight));
    setSaveError(null);
    setRationale('');
    setEditing(false);
  }

  const label = entry.is_module_level
    ? `[module] ${entry.child_module_display_name ?? entry.child_module_code ?? '—'}`
    : entry.parameter_display_name ?? entry.parameter_code ?? '—';

  const previewResolved = editing ? resolveWeightFromSelection() : { weight: 0, error: null };

  return (
    <tr className="border-t border-ink-100 hover:bg-ink-50/30 align-top">
      <td className="px-4 py-2 text-ink-700">
        {label}
        {entry.is_module_level && (
          <span className="ml-1 text-[9px] text-purple-600 uppercase">integrated</span>
        )}
      </td>
      <td className="px-4 py-2 font-mono text-ink-400 text-[10px]">
        {entry.parameter_code ?? entry.child_module_code ?? '—'}
      </td>
      <td className="px-4 py-2 w-56">
        {editing ? (
          <div className="space-y-1">
            <select
              value={selectedPct}
              onChange={(e) => setSelectedPct(Number(e.target.value))}
              className="w-full text-xs border border-accent rounded-sm px-2 py-1 bg-white"
              autoFocus
            >
              {CONTRIBUTION_PRESETS.map((p) => (
                <option key={p.pct} value={p.pct}>{p.label}</option>
              ))}
              <option value={CUSTOM_OPTION.pct}>{CUSTOM_OPTION.label}</option>
            </select>
            {selectedPct === CUSTOM_OPTION.pct && (
              <input
                type="number"
                step="0.01"
                min="0"
                max="9999"
                value={customDraft}
                onChange={(e) => setCustomDraft(e.target.value)}
                className="w-full text-xs border border-ink-300 rounded-sm px-2 py-1 font-mono"
              />
            )}
            {/* A weighting decision nobody recorded a reason for cannot be
                defended later. The rationale is stored on the weight entry and
                travels with the assessments that used it. */}
            <textarea
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              rows={2}
              className="w-full text-xs border border-ink-300 rounded-sm px-2 py-1 leading-snug"
              placeholder="Why is this weight changing? (recorded against the assessment)"
              onKeyDown={(e) => {
                if (e.key === 'Escape') handleCancel();
              }}
            />
            {!previewResolved.error && (
              <div className="text-[10px] text-ink-400 font-mono">
                → weight = {formatWeight(previewResolved.weight)}
              </div>
            )}
            {saveError && <div className="text-[10px] text-red-600">{saveError}</div>}
          </div>
        ) : (
          <div>
            <div className="text-sm text-ink-700">
              {currentPct === null ? (
                <span className="text-ink-400 italic">—</span>
              ) : entry.weight === 0 ? (
                'Disabled'
              ) : (
                <>~{currentPct.toFixed(0)}% of group</>
              )}
            </div>
            <div className="text-[10px] font-mono text-ink-400">
              raw weight = {formatWeight(entry.weight)}
            </div>
          </div>
        )}
      </td>
      <td className="px-4 py-2 w-28">
        {editing ? (
          <div className="flex gap-1">
            <button
              type="button"
              onClick={handleSave}
              disabled={mutation.isPending || !!previewResolved.error}
              className="text-[11px] px-2 py-0.5 bg-accent text-white rounded-sm hover:bg-accent/90 disabled:opacity-50"
            >
              {mutation.isPending ? '…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="text-[11px] px-2 py-0.5 text-ink-600 border border-ink-200 rounded-sm"
            >
              Cancel
            </button>
          </div>
        ) : readOnly ? null : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-[11px] text-accent hover:underline"
          >
            Edit
          </button>
        )}
      </td>
    </tr>
  );
}

// =============================================================================
// Small shared components
// =============================================================================

function RolePill({ role }: { role: string }) {
  const colors: Record<string, string> = {
    Input: 'bg-blue-50 text-blue-700 border-blue-200',
    Matrix_axis_1: 'bg-purple-50 text-purple-700 border-purple-200',
    Matrix_axis_2: 'bg-pink-50 text-pink-700 border-pink-200',
    Matrix_axis_3: 'bg-rose-50 text-rose-700 border-rose-200',
    Rule_variable: 'bg-amber-50 text-amber-700 border-amber-200',
    Filter: 'bg-teal-50 text-teal-700 border-teal-200',
    Exclusion: 'bg-red-50 text-red-700 border-red-200',
  };
  const cls = colors[role] ?? 'bg-ink-50 text-ink-500 border-ink-200';
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${cls}`}>
      {role.replace(/_/g, ' ')}
    </span>
  );
}

function Loading() {
  return <div className="text-sm text-ink-400 italic py-6">Loading…</div>;
}

function ErrorPanel({ error }: { error: unknown }) {
  const msg = error instanceof Error ? error.message : String(error);
  return (
    <div className="border border-red-200 bg-red-50 text-red-700 rounded-sm p-4 text-sm">
      <div className="font-medium mb-1">Failed to load.</div>
      <div className="text-xs font-mono">{msg}</div>
    </div>
  );
}